"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, type ComponentProps } from "react";
import type { MapView } from "./MapView";

/** Doit rester aligné sur le `lg:` de Tailwind (défaut 64rem = 1024px). */
const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Lazy wrapper pour MapView.
 *
 * ssr: false → MapView utilise window/leaflet (DOM only). Server Components ne
 * peuvent pas passer ssr:false à next/dynamic depuis Next 15, d'où ce wrapper
 * Client Component. Le placeholder match la hauteur de la sticky map pour éviter
 * tout CLS sur desktop.
 */
const MapViewInner = dynamic(
  () => import("./MapView").then((m) => ({ default: m.MapView })),
  {
    ssr: false,
    loading: () => <MapPlaceholder />,
  },
);

function MapPlaceholder() {
  return (
    <div
      className="h-full w-full animate-pulse bg-[var(--sahha-neutral-100,#F1F5F9)]"
      aria-hidden
    />
  );
}

export function MapViewLazy(props: ComponentProps<typeof MapView>) {
  // Porte desktop. Le seul appelant (/recherche) place la carte dans un
  // <aside className="hidden lg:block"> : sous 1024px elle est display:none.
  // Or `dynamic()` ne regarde pas le CSS — il monte dès l'hydratation, quelle
  // que soit la largeur. Le visiteur mobile téléchargeait donc maplibre-gl
  // (~216 Ko brotli) PUIS les tuiles vectorielles, sprites et glyphes
  // (~550 Ko réseau) pour une carte qu'il ne verra jamais.
  //
  // Mesuré le 2026-09-10 sur build de production, /recherche?ville=casablanca
  // en 390x844 : ~818 Ko épargnés par visite mobile. /recherche est la page
  // la plus exposée du site, et les CWV mobile sont déjà un point ouvert.
  //
  // Départ à false : le premier rendu client ne monte rien, puis l'effet
  // tranche. Sur desktop cela coûte un cycle de rendu avant le mount, invisible
  // à l'œil, et le placeholder occupe déjà la hauteur finale (pas de CLS).
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    // Suit les rotations d'écran et les redimensionnements de fenêtre.
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  if (!isDesktop) return <MapPlaceholder />;

  return <MapViewInner {...props} />;
}
